import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import { Users, Clock, TrendingUp, AlertTriangle, List, Table, Play, MessageCircle, FileText, ClipboardCheck, Search, Plus, Edit, Trash2, Send, Filter, Download, Columns, MoreHorizontal, Volume2, ChevronUp, ChevronDown, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import VideoPlayer from '../components/VideoPlayer';
import './PatientOverview.css';

const PatientOverview = () => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [chartType, setChartType] = useState('adherence'); // 'doseTime', 'adherence', 'videos', 'chats', 'notes', 'reconciliation'
  const [viewType, setViewType] = useState('detailed'); // 'detailed' or 'simple'
  const [timeFilter, setTimeFilter] = useState('6months'); // '3months', '6months', '1year'
  const [selectedNoteId, setSelectedNoteId] = useState(1);
  const [notesSearchTerm, setNotesSearchTerm] = useState('');
  const [selectedChatId, setSelectedChatId] = useState(1);
  const [chatSearchTerm, setChatSearchTerm] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [patientListHeight, setPatientListHeight] = useState(320); // Default height for ~4.5 patients
  const [selectedMedications, setSelectedMedications] = useState({
    'Atorvastatin': true,
    'Metformin': true,
    'Lisinopril': true,
    'Sertraline': true,
    'Levothyroxine': true,
    'Advil': true,
    'Tylenol': true,
    'Aspirin': true,
    'Ibuprofen': true,
    'Amoxicillin': true
  });
  const [patientSortConfig, setPatientSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All selected (7)');
  const [segmentFilter, setSegmentFilter] = useState('Any segment');
  const [tableTimeFilter, setTableTimeFilter] = useState('Last 7 days');

  // Enhanced patient data with all required fields for the table - 22 total patients
  const patients = [
    // Patients with alerts (will be sorted first)
    { 
      id: 42, 
      name: 'Patient 42', 
      status: 'online', 
      adherence: 85, 
      lastDose: '2 hours ago',
      scheduleAdherence: '15.9%',
      dosingOpportunities: '219 / 1376',
      daysInTreatment: 1023,
      doseAdminTime: -198,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 8, 2025',
      weekTrend: '+10.3%',
      hasAlerts: true,
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 44, 
      name: 'Patient 44', 
      status: 'offline', 
      adherence: 45, 
      lastDose: '3 hours ago',
      scheduleAdherence: '10.3%',
      dosingOpportunities: '142 / 1376',
      daysInTreatment: 612,
      doseAdminTime: 85,
      pillboxStatus: 'Offline',
      nextFollowUp: 'Dec 5, 2025',
      weekTrend: '-2.8%',
      hasAlerts: true,
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 48, 
      name: 'Patient 48', 
      status: 'online', 
      adherence: 38, 
      lastDose: '4 hours ago',
      scheduleAdherence: '1.3%',
      dosingOpportunities: '89 / 1376',
      daysInTreatment: 445,
      doseAdminTime: 156,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 6, 2025',
      weekTrend: '-15.2%',
      hasAlerts: true,
      avatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM5Q0EzQUYiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0iI0Y5RkFGQiIvPgo8cGF0aCBkPSJNMzIgMzJDMzIgMjYuNDc3MiAyNy41MjI4IDIyIDIyIDIySDEyQzYuNDc3MTUgMjIgMiAyNi40NzcyIDIgMzJWNDBIMzJWMzJaIiBmaWxsPSIjRjlGQUZCIi8+Cjwvc3ZnPgo='
    },
    { 
      id: 52, 
      name: 'Patient 52', 
      status: 'offline', 
      adherence: 32, 
      lastDose: '8 hours ago',
      scheduleAdherence: '8.7%',
      dosingOpportunities: '67 / 1376',
      daysInTreatment: 234,
      doseAdminTime: 245,
      pillboxStatus: 'Offline',
      nextFollowUp: 'Dec 3, 2025',
      weekTrend: '-22.1%',
      hasAlerts: true,
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 57, 
      name: 'Patient 57', 
      status: 'not_assigned', 
      adherence: 18, 
      lastDose: '2 days ago',
      scheduleAdherence: '5.2%',
      dosingOpportunities: '34 / 1376',
      daysInTreatment: 89,
      doseAdminTime: 0,
      pillboxStatus: 'Not Assigned',
      nextFollowUp: 'Dec 2, 2025',
      weekTrend: '-8.9%',
      hasAlerts: true,
      avatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM5Q0EzQUYiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0iI0Y5RkFGQiIvPgo8cGF0aCBkPSJNMzIgMzJDMzIgMjYuNDc3MiAyNy41MjI4IDIyIDIyIDIySDEyQzYuNDc3MTUgMjIgMiAyNi40NzcyIDIgMzJWNDBIMzJWMzJaIiBmaWxsPSIjRjlGQUZCIi8+Cjwvc3ZnPgo='
    },
    { 
      id: 63, 
      name: 'Patient 63', 
      status: 'online', 
      adherence: 28, 
      lastDose: '6 hours ago',
      scheduleAdherence: '12.4%',
      dosingOpportunities: '156 / 1376',
      daysInTreatment: 567,
      doseAdminTime: 189,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 4, 2025',
      weekTrend: '-18.7%',
      hasAlerts: true,
      avatar: 'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    // Patients without alerts
    { 
      id: 43, 
      name: 'Patient 43', 
      status: 'offline', 
      adherence: 72, 
      lastDose: '1 day ago',
      scheduleAdherence: '20.7%',
      dosingOpportunities: '312 / 1376',
      daysInTreatment: 856,
      doseAdminTime: -45,
      pillboxStatus: 'Offline',
      nextFollowUp: 'Dec 10, 2025',
      weekTrend: '+5.2%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 45, 
      name: 'Patient 45', 
      status: 'online', 
      adherence: 92, 
      lastDose: '1 hour ago',
      scheduleAdherence: '100%',
      dosingOpportunities: '1350 / 1376',
      daysInTreatment: 1205,
      doseAdminTime: -12,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 15, 2025',
      weekTrend: '+2.1%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 46, 
      name: 'Patient 46', 
      status: 'not_assigned', 
      adherence: 0, 
      lastDose: 'Never',
      scheduleAdherence: '15.3%',
      dosingOpportunities: '0 / 0',
      daysInTreatment: 0,
      doseAdminTime: 0,
      pillboxStatus: 'Not Assigned',
      nextFollowUp: 'Pending',
      weekTrend: '0%',
      hasAlerts: false,
      avatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM5Q0EzQUYiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0iI0Y5RkFGQiIvPgo8cGF0aCBkPSJNMzIgMzJDMzIgMjYuNDc3MiAyNy41MjI4IDIyIDIyIDIySDEyQzYuNDc3MTUgMjIgMiAyNi40NzcyIDIgMzJWNDBIMzJWMzJaIiBmaWxsPSIjRjlGQUZCIi8+Cjwvc3ZnPgo='
    },
    { 
      id: 47, 
      name: 'Patient 47', 
      status: 'online', 
      adherence: 88, 
      lastDose: '30 min ago',
      scheduleAdherence: '35.8%',
      dosingOpportunities: '1205 / 1376',
      daysInTreatment: 892,
      doseAdminTime: -28,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 12, 2025',
      weekTrend: '+8.7%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 49, 
      name: 'Patient 49', 
      status: 'online', 
      adherence: 83, 
      lastDose: '45 min ago',
      scheduleAdherence: '78.9%',
      dosingOpportunities: '1087 / 1376',
      daysInTreatment: 934,
      doseAdminTime: -23,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 11, 2025',
      weekTrend: '+4.6%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1463453091185-61582044d556?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 50, 
      name: 'Patient 50', 
      status: 'offline', 
      adherence: 67, 
      lastDose: '2 hours ago',
      scheduleAdherence: '45.2%',
      dosingOpportunities: '623 / 1376',
      daysInTreatment: 678,
      doseAdminTime: 34,
      pillboxStatus: 'Offline',
      nextFollowUp: 'Dec 13, 2025',
      weekTrend: '-1.8%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 51, 
      name: 'Patient 51', 
      status: 'online', 
      adherence: 91, 
      lastDose: '20 min ago',
      scheduleAdherence: '89.4%',
      dosingOpportunities: '1231 / 1376',
      daysInTreatment: 1156,
      doseAdminTime: -8,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 18, 2025',
      weekTrend: '+1.3%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 53, 
      name: 'Patient 53', 
      status: 'online', 
      adherence: 79, 
      lastDose: '1.2 hours ago',
      scheduleAdherence: '56.7%',
      dosingOpportunities: '781 / 1376',
      daysInTreatment: 743,
      doseAdminTime: 15,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 14, 2025',
      weekTrend: '+3.2%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 54, 
      name: 'Patient 54', 
      status: 'offline', 
      adherence: 58, 
      lastDose: '3.5 hours ago',
      scheduleAdherence: '34.1%',
      dosingOpportunities: '469 / 1376',
      daysInTreatment: 456,
      doseAdminTime: 67,
      pillboxStatus: 'Offline',
      nextFollowUp: 'Dec 7, 2025',
      weekTrend: '+0.9%',
      hasAlerts: false,
      avatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM5Q0EzQUYiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0iI0Y5RkFGQiIvPgo8cGF0aCBkPSJNMzIgMzJDMzIgMjYuNDc3MiAyNy41MjI4IDIyIDIyIDIySDEyQzYuNDc3MTUgMjIgMiAyNi40NzcyIDIgMzJWNDBIMzJWMzJaIiBmaWxsPSIjRjlGQUZCIi8+Cjwvc3ZnPgo='
    },
    { 
      id: 55, 
      name: 'Patient 55', 
      status: 'online', 
      adherence: 84, 
      lastDose: '25 min ago',
      scheduleAdherence: '71.3%',
      dosingOpportunities: '982 / 1376',
      daysInTreatment: 823,
      doseAdminTime: -18,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 16, 2025',
      weekTrend: '+2.7%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 56, 
      name: 'Patient 56', 
      status: 'online', 
      adherence: 77, 
      lastDose: '1.8 hours ago',
      scheduleAdherence: '52.6%',
      dosingOpportunities: '724 / 1376',
      daysInTreatment: 634,
      doseAdminTime: 28,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 17, 2025',
      weekTrend: '+1.8%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 58, 
      name: 'Patient 58', 
      status: 'offline', 
      adherence: 69, 
      lastDose: '4.2 hours ago',
      scheduleAdherence: '41.8%',
      dosingOpportunities: '576 / 1376',
      daysInTreatment: 512,
      doseAdminTime: 89,
      pillboxStatus: 'Offline',
      nextFollowUp: 'Dec 9, 2025',
      weekTrend: '-0.3%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 59, 
      name: 'Patient 59', 
      status: 'online', 
      adherence: 86, 
      lastDose: '35 min ago',
      scheduleAdherence: '73.2%',
      dosingOpportunities: '1007 / 1376',
      daysInTreatment: 892,
      doseAdminTime: -14,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 19, 2025',
      weekTrend: '+3.8%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 60, 
      name: 'Patient 60', 
      status: 'online', 
      adherence: 74, 
      lastDose: '55 min ago',
      scheduleAdherence: '48.9%',
      dosingOpportunities: '673 / 1376',
      daysInTreatment: 698,
      doseAdminTime: 12,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 20, 2025',
      weekTrend: '+2.4%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 61, 
      name: 'Patient 61', 
      status: 'online', 
      adherence: 76, 
      lastDose: '1.5 hours ago',
      scheduleAdherence: '42.5%',
      dosingOpportunities: '856 / 1376',
      daysInTreatment: 723,
      doseAdminTime: 22,
      pillboxStatus: 'Online',
      nextFollowUp: 'Dec 9, 2025',
      weekTrend: '+6.4%',
      hasAlerts: false,
      avatar: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=40&h=40&fit=crop&crop=face&auto=format'
    },
    { 
      id: 62, 
      name: 'Patient 62', 
      status: 'offline', 
      adherence: 64, 
      lastDose: '2.7 hours ago',
      scheduleAdherence: '37.4%',
      dosingOpportunities: '515 / 1376',
      daysInTreatment: 423,
      doseAdminTime: 56,
      pillboxStatus: 'Offline',
      nextFollowUp: 'Dec 8, 2025',
      weekTrend: '+1.1%',
      hasAlerts: false,
      avatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM5Q0EzQUYiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0iI0Y5RkFGQiIvPgo8cGF0aCBkPSJNMzIgMzJDMzIgMjYuNDc3MiAyNy41MjI4IDIyIDIyIDIySDEyQzYuNDc3MTUgMjIgMiAyNi40NzcyIDIgMzJWNDBIMzJWMzJaIiBmaWxsPSIjRjlGQUZCIi8+Cjwvc3ZnPgo='
    }
  ];

  // Patient sorting functions
  const handlePatientSort = (key) => {
    let direction = 'asc';
    if (patientSortConfig.key === key && patientSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setPatientSortConfig({ key, direction });
  };

  const getPatientSortValue = (patient, key) => {
    switch (key) {
      case 'id':
        return patient.id;
      case 'weekTrend':
        return parseFloat(patient.weekTrend?.replace('%', '').replace('+', '') || '0');
      case 'scheduleAdherence':
        return parseFloat(patient.scheduleAdherence.replace('%', ''));
      case 'overallAdherence':
        return patient.adherence;
      case 'dosingOpportunities':
        return parseInt(patient.dosingOpportunities.split(' / ')[0]);
      case 'daysInTreatment':
        return patient.daysInTreatment;
      case 'doseAdminTime':
        return patient.doseAdminTime;
      case 'pillboxStatus':
        return patient.status;
      case 'nextFollowUp':
        return new Date(patient.nextFollowUp || '2025-12-08');
      default:
        return patient[key];
    }
  };

  const renderPatientSortIcon = (columnKey) => {
    if (patientSortConfig.key !== columnKey) {
      return (
        <span className="sort-icons">
          <ChevronUp size={12} className="sort-icon inactive" />
          <ChevronDown size={12} className="sort-icon inactive" />
        </span>
      );
    }
    
    return (
      <span className="sort-icons">
        <ChevronUp size={12} className={`sort-icon ${patientSortConfig.direction === 'asc' ? 'active' : 'inactive'}`} />
        <ChevronDown size={12} className={`sort-icon ${patientSortConfig.direction === 'desc' ? 'active' : 'inactive'}`} />
      </span>
    );
  };

  // Sort patients with alerts first, then by sorting config
  const sortedPatients = patients.sort((a, b) => {
    // First, sort by alerts if no custom sort is applied
    if (!patientSortConfig.key) {
      if (a.hasAlerts && !b.hasAlerts) return -1;
      if (!a.hasAlerts && b.hasAlerts) return 1;
      return a.id - b.id; // Then by ID for consistent ordering
    }
    
    // Apply custom sorting
    const aValue = getPatientSortValue(a, patientSortConfig.key);
    const bValue = getPatientSortValue(b, patientSortConfig.key);
    
    if (aValue < bValue) {
      return patientSortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return patientSortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Mock dose administration time data
  const getDoseData = (patientId) => {
    const baseData = [
      { time: -120, allPatients: 8, selectedPatient: 0 },
      { time: -90, allPatients: 10, selectedPatient: 0 },
      { time: -60, allPatients: 5, selectedPatient: 0 },
      { time: -30, allPatients: 15, selectedPatient: 0 },
      { time: -15, allPatients: 25, selectedPatient: 0 },
      { time: 0, allPatients: 60, selectedPatient: 0 },
      { time: 15, allPatients: 35, selectedPatient: 0 },
      { time: 30, allPatients: 15, selectedPatient: 0 },
      { time: 60, allPatients: 8, selectedPatient: 0 },
      { time: 90, allPatients: 5, selectedPatient: 0 },
      { time: 120, allPatients: 3, selectedPatient: 0 }
    ];

    if (!patientId) return baseData;

    // Generate different patterns based on patient
    return baseData.map(point => {
      let selectedValue = 0;
      
      switch (patientId) {
        case 42: // Patient 42 - mostly on time
          if (point.time >= -15 && point.time <= 15) {
            selectedValue = point.allPatients * 0.8;
          } else if (point.time >= -30 && point.time <= 30) {
            selectedValue = point.allPatients * 0.3;
          }
          break;
        case 44: // Patient 44 - often late
          if (point.time >= 15 && point.time <= 60) {
            selectedValue = point.allPatients * 0.7;
          } else if (point.time >= 0 && point.time <= 90) {
            selectedValue = point.allPatients * 0.4;
          }
          break;
        case 45: // Patient 45 - very punctual
          if (point.time >= -5 && point.time <= 5) {
            selectedValue = point.allPatients * 0.9;
          } else if (point.time >= -15 && point.time <= 15) {
            selectedValue = point.allPatients * 0.2;
          }
          break;
        case 48: // Patient 48 - irregular timing
          if (point.time >= 30 && point.time <= 120) {
            selectedValue = point.allPatients * 0.6;
          } else if (point.time >= -60 && point.time <= -30) {
            selectedValue = point.allPatients * 0.4;
          }
          break;
        case 61: // Patient 61 - slightly late tendency
          if (point.time >= 0 && point.time <= 30) {
            selectedValue = point.allPatients * 0.6;
          } else if (point.time >= 15 && point.time <= 45) {
            selectedValue = point.allPatients * 0.4;
          }
          break;
        default:
          selectedValue = point.allPatients * 0.5;
      }
      
      return {
        ...point,
        selectedPatient: Math.round(selectedValue)
      };
    });
  };

  // All available medications with colors
  const allMedications = {
    'Atorvastatin': { color: '#4F46E5', baseAdherence: 78 },
    'Metformin': { color: '#EF4444', baseAdherence: 74 },
    'Lisinopril': { color: '#8B5CF6', baseAdherence: 82 },
    'Sertraline': { color: '#F59E0B', baseAdherence: 71 },
    'Levothyroxine': { color: '#EC4899', baseAdherence: 89 },
    'Advil': { color: '#3B82F6', baseAdherence: 68 },
    'Tylenol': { color: '#10B981', baseAdherence: 85 },
    'Aspirin': { color: '#8B5CF6', baseAdherence: 73 },
    'Ibuprofen': { color: '#06B6D4', baseAdherence: 77 },
    'Amoxicillin': { color: '#A855F7', baseAdherence: 91 },
    'Omeprazole': { color: '#F97316', baseAdherence: 80 },
    'Simvastatin': { color: '#14B8A6', baseAdherence: 76 },
    'Amlodipine': { color: '#6366F1', baseAdherence: 84 },
    'Gabapentin': { color: '#EAB308', baseAdherence: 69 },
    'Hydrochlorothiazide': { color: '#84CC16', baseAdherence: 82 }
  };

  // Patient-specific medications
  const patientMedications = {
    42: ['Lisinopril', 'Metformin', 'Atorvastatin', 'Advil', 'Tylenol'],
    44: ['Lisinopril', 'Metformin', 'Omeprazole'],
    45: ['Sertraline', 'Gabapentin', 'Ibuprofen', 'Tylenol'],
    47: ['Amlodipine', 'Hydrochlorothiazide', 'Aspirin'],
    48: ['Atorvastatin', 'Levothyroxine', 'Simvastatin'],
    43: ['Metformin', 'Lisinopril', 'Atorvastatin', 'Aspirin'],
    63: ['Atorvastatin', 'Levothyroxine', 'Sertraline', 'Metformin'],
    50: ['Amoxicillin', 'Ibuprofen', 'Tylenol'],
    53: ['Levothyroxine', 'Omeprazole'],
    54: ['Metformin', 'Simvastatin', 'Amlodipine'],
    55: ['Lisinopril', 'Atorvastatin', 'Aspirin', 'Omeprazole'],
    56: ['Gabapentin', 'Sertraline', 'Tylenol'],
    58: ['Amlodipine', 'Hydrochlorothiazide', 'Metformin'],
    59: ['Levothyroxine', 'Atorvastatin', 'Lisinopril'],
    60: ['Sertraline', 'Omeprazole', 'Ibuprofen']
  };

  // Get medications for the selected patient
  const getPatientMedications = (patientId) => {
    const medicationNames = patientMedications[patientId] || ['Metformin', 'Lisinopril'];
    return medicationNames.map(name => ({
      name,
      color: allMedications[name].color,
      baseAdherence: allMedications[name].baseAdherence
    }));
  };

  // Current medications for display (based on selected patient)
  const medications = selectedPatient ? getPatientMedications(selectedPatient.id) : [];

  // Mock medication adherence data over time (last 6 months)
  const getMedicationAdherenceData = () => {
    const months = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];
    const data = [];

    months.forEach((month, monthIndex) => {
      const dataPoint = { month };
      
      medications.forEach((med) => {
        // Generate adherence data with some variability over time
        const trend = Math.sin(monthIndex * 0.5) * 5; // Seasonal variation
        const noise = (Math.random() - 0.5) * 10; // Random variation
        const adherence = Math.max(50, Math.min(100, med.baseAdherence + trend + noise));
        dataPoint[med.name] = Math.round(adherence);
      });
      
      data.push(dataPoint);
    });
    
    return data;
  };

  // Mock adherence data over time (last 30 days) - for patient comparison
  const getAdherenceData = (patientId) => {
    const days = [];
    const today = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayName = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      // Generate baseline adherence for all patients (average around 75%)
      const allPatientsAdherence = 70 + Math.random() * 20 + Math.sin(i * 0.2) * 10;

      let selectedPatientAdherence = 0;

      if (patientId) {
        switch (patientId) {
          case 42: // Patient 42 - good adherence with some variability
            selectedPatientAdherence = 80 + Math.random() * 15 + Math.sin(i * 0.3) * 8;
            break;
          case 43: // Patient 43 - moderate adherence
            selectedPatientAdherence = 65 + Math.random() * 20 + Math.sin(i * 0.25) * 12;
            break;
          case 44: // Patient 44 - poor adherence
            selectedPatientAdherence = 35 + Math.random() * 25 + Math.sin(i * 0.4) * 15;
            break;
          case 45: // Patient 45 - excellent adherence
            selectedPatientAdherence = 90 + Math.random() * 8 + Math.sin(i * 0.15) * 5;
            break;
          case 47: // Patient 47 - good but declining
            selectedPatientAdherence = 90 - (i * 0.8) + Math.random() * 10;
            break;
          case 48: // Patient 48 - very poor adherence
            selectedPatientAdherence = 20 + Math.random() * 30 + Math.sin(i * 0.5) * 10;
            break;
          case 61: // Patient 61 - improving adherence
            selectedPatientAdherence = 50 + (i * 0.5) + Math.random() * 15;
            break;
          default:
            selectedPatientAdherence = allPatientsAdherence + (Math.random() - 0.5) * 20;
        }
        
        // Ensure values stay within reasonable bounds
        selectedPatientAdherence = Math.max(0, Math.min(100, selectedPatientAdherence));
      }
      
      days.push({
        day: dayName,
        allPatients: Math.round(allPatientsAdherence),
        selectedPatient: Math.round(selectedPatientAdherence)
      });
    }
    
    return days;
  };

  const chartData = chartType === 'doseTime' 
    ? getDoseData(selectedPatient?.id) 
    : getMedicationAdherenceData();

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    
    // Initialize selected medications for the new patient (all checked by default)
    if (patient) {
      const patientMeds = patientMedications[patient.id] || ['Metformin', 'Lisinopril'];
      const newSelectedMedications = {};
      patientMeds.forEach(medName => {
        newSelectedMedications[medName] = true;
      });
      setSelectedMedications(newSelectedMedications);
    }
  };

  const handleChartTypeChange = (type) => {
    setChartType(type);
  };

  const handleMedicationToggle = (medicationName) => {
    setSelectedMedications(prev => ({
      ...prev,
      [medicationName]: !prev[medicationName]
    }));
  };

  const handleSelectAll = () => {
    const newState = {};
    medications.forEach(med => {
      newState[med.name] = true;
    });
    setSelectedMedications(newState);
  };

  const handleSelectNone = () => {
    const newState = {};
    medications.forEach(med => {
      newState[med.name] = false;
    });
    setSelectedMedications(newState);
  };

  // Mock notes data
  const notes = [
    {
      id: 1,
      title: "Patient Review Report",
      date: "Aug 28, 2025, 8:45 PM",
      author: "Thatiane Gellman",
      patientId: 63,
      summary: "Summary of Findings",
      content: `1. Medication Dispensing Behavior: Over the past month, patient has dispensed medication on a regular basis using the HiDo device. On a couple occasions, the patient appeared to have skipped taking the medication, despite actions suggested otherwise. Specifically, the medication cup was empty, but there were indications that the pills were not ingested.

2. Additional Medication Consumption: The patient has been observed taking extra pills during the morning schedule, in addition to those dispensed by the HiDO device. Making a total of 9 pills taken during the morning period.

3. Observational Insights: When the patient takes their medication and drinks water from a clear water bottle, it provides greater visibility. This allows for more reliable monitoring to ensure the patient is consuming their medication effectively, with approximately 99% certainty of adherence when observed.`
    },
    {
      id: 2,
      title: "Patient Review Report",
      date: "Aug 27, 2025, 2:30 PM",
      author: "Dr. Smith",
      patientId: 42,
      summary: "Weekly Assessment",
      content: "Patient showing excellent adherence to medication schedule. No issues observed with the HiDo device functionality. Recommend continuing current protocol."
    },
    {
      id: 3,
      title: "Patient Review Report",
      date: "Aug 26, 2025, 10:15 AM",
      author: "Nurse Johnson",
      patientId: 44,
      summary: "Medication Compliance Review",
      content: "Patient missed several doses this week. Discussed importance of adherence with patient and family. Will schedule follow-up consultation."
    },
    {
      id: 4,
      title: "Patient Review Report",
      date: "Aug 25, 2025, 4:20 PM",
      author: "Dr. Williams",
      patientId: 45,
      summary: "Monthly Progress Note",
      content: "Patient demonstrates consistent medication adherence. Device functioning properly. Patient reports feeling well and no adverse effects noted."
    },
    {
      id: 5,
      title: "Patient Review Report",
      date: "Aug 24, 2025, 11:30 AM",
      author: "Thatiane Gellman",
      patientId: 48,
      summary: "Behavioral Observation",
      content: "Noted patient tendency to delay medication intake. Recommend reminder system enhancement. Overall compliance improving."
    },
    {
      id: 6,
      title: "Patient Review Report",
      date: "Aug 23, 2025, 3:45 PM",
      author: "Dr. Chen",
      patientId: 47,
      summary: "Device Performance Review",
      content: "HiDo device operating within normal parameters. Patient comfortable with technology. No technical issues reported."
    },
    {
      id: 7,
      title: "Patient Review Report",
      date: "Aug 22, 2025, 9:10 AM",
      author: "Nurse Davis",
      patientId: 43,
      summary: "Care Plan Update",
      content: "Adjusted medication schedule based on patient preference. Updated care plan to reflect changes. Patient education provided."
    }
  ];

  const handleNoteSelect = (noteId) => {
    setSelectedNoteId(noteId);
  };

  // Get notes for the selected patient
  const getPatientNotes = (patientId) => {
    if (!patientId) return [];
    return notes.filter(note => note.patientId === patientId);
  };

  const filteredNotes = selectedPatient 
    ? getPatientNotes(selectedPatient.id).filter(note =>
        note.title.toLowerCase().includes(notesSearchTerm.toLowerCase()) ||
        note.author.toLowerCase().includes(notesSearchTerm.toLowerCase()) ||
        note.summary.toLowerCase().includes(notesSearchTerm.toLowerCase())
      )
    : [];

  // Mock chat data
  const chats = [
    {
      id: 1,
      patientId: 42,
      patientName: "Patient 42",
      lastMessage: "Thank you for adjusting my medication schedule.",
      lastMessageTime: "2:30 PM",
      unreadCount: 0,
      isOnline: true,
      messages: [
        {
          id: 1,
          sender: "patient",
          senderName: "Patient 42",
          message: "Good morning, Dr. Smith. I've been taking my medications as prescribed, but I'm experiencing some side effects.",
          timestamp: "Today, 9:15 AM",
          time: "9:15 AM"
        },
        {
          id: 2,
          sender: "doctor",
          senderName: "Dr. Smith",
          message: "Good morning! Thank you for reaching out. Can you describe the side effects you're experiencing?",
          timestamp: "Today, 9:22 AM",
          time: "9:22 AM"
        },
        {
          id: 3,
          sender: "patient",
          senderName: "Patient 42",
          message: "I've been feeling dizzy in the mornings, especially after taking the Lisinopril. It usually lasts for about an hour.",
          timestamp: "Today, 9:28 AM",
          time: "9:28 AM"
        },
        {
          id: 4,
          sender: "doctor",
          senderName: "Dr. Smith",
          message: "That's a known side effect of Lisinopril, especially when starting treatment. Let's adjust your schedule. Try taking it in the evening instead of morning.",
          timestamp: "Today, 2:15 PM",
          time: "2:15 PM"
        },
        {
          id: 5,
          sender: "patient",
          senderName: "Patient 42",
          message: "Thank you for adjusting my medication schedule.",
          timestamp: "Today, 2:30 PM",
          time: "2:30 PM"
        }
      ]
    },
    {
      id: 2,
      patientId: 44,
      patientName: "Patient 44",
      lastMessage: "I'll make sure to take them on time from now on.",
      lastMessageTime: "Yesterday",
      unreadCount: 0,
      isOnline: false,
      messages: [
        {
          id: 1,
          sender: "doctor",
          senderName: "Dr. Smith",
          message: "Hi there! I noticed you've missed a few doses this week. Is everything okay?",
          timestamp: "Yesterday, 10:30 AM",
          time: "10:30 AM"
        },
        {
          id: 2,
          sender: "patient",
          senderName: "Patient 44",
          message: "Sorry doctor, I've been having trouble remembering to take them. The HiDo device helps but sometimes I'm away from home.",
          timestamp: "Yesterday, 11:45 AM",
          time: "11:45 AM"
        },
        {
          id: 3,
          sender: "doctor",
          senderName: "Dr. Smith",
          message: "No worries! Let's set up additional reminders on your phone. Consistency is important for your treatment.",
          timestamp: "Yesterday, 12:15 PM",
          time: "12:15 PM"
        },
        {
          id: 4,
          sender: "patient",
          senderName: "Patient 44",
          message: "I'll make sure to take them on time from now on.",
          timestamp: "Yesterday, 3:20 PM",
          time: "3:20 PM"
        }
      ]
    },
    {
      id: 3,
      patientId: 45,
      patientName: "Patient 45",
      lastMessage: "Perfect! The new schedule works much better for me.",
      lastMessageTime: "Aug 27",
      unreadCount: 0,
      isOnline: true,
      messages: [
        {
          id: 1,
          sender: "patient",
          senderName: "Patient 45",
          message: "Dr. Williams, I wanted to ask about the possibility of adjusting my medication schedule. The current timing conflicts with my work.",
          timestamp: "Aug 27, 2:00 PM",
          time: "2:00 PM"
        },
        {
          id: 2,
          sender: "doctor",
          senderName: "Dr. Williams",
          message: "Of course! What would work better for your schedule? We have some flexibility with most of your medications.",
          timestamp: "Aug 27, 2:30 PM",
          time: "2:30 PM"
        },
        {
          id: 3,
          sender: "patient",
          senderName: "Patient 45",
          message: "Could we move the afternoon dose to early evening? Around 6 PM would be ideal.",
          timestamp: "Aug 27, 3:15 PM",
          time: "3:15 PM"
        },
        {
          id: 4,
          sender: "doctor",
          senderName: "Dr. Williams",
          message: "That works perfectly! I've updated your schedule in the system. The HiDo device will adjust automatically.",
          timestamp: "Aug 27, 4:00 PM",
          time: "4:00 PM"
        },
        {
          id: 5,
          sender: "patient",
          senderName: "Patient 45",
          message: "Perfect! The new schedule works much better for me.",
          timestamp: "Aug 27, 4:05 PM",
          time: "4:05 PM"
        }
      ]
    },
    {
      id: 4,
      patientId: 48,
      patientName: "Patient 48",
      lastMessage: "When would be a good time for a check-up?",
      lastMessageTime: "Aug 26",
      unreadCount: 2,
      isOnline: false,
      messages: [
        {
          id: 1,
          sender: "doctor",
          senderName: "Dr. Chen",
          message: "Hello! I've been reviewing your medication adherence data. There are some concerns I'd like to discuss.",
          timestamp: "Aug 26, 9:00 AM",
          time: "9:00 AM"
        },
        {
          id: 2,
          sender: "patient",
          senderName: "Patient 48",
          message: "Hi Dr. Chen. I know I've been struggling with consistency. What are your main concerns?",
          timestamp: "Aug 26, 2:15 PM",
          time: "2:15 PM"
        },
        {
          id: 3,
          sender: "doctor",
          senderName: "Dr. Chen",
          message: "Your adherence has dropped to 38% this month. This could impact your treatment effectiveness. Would you like to schedule a consultation?",
          timestamp: "Aug 26, 3:30 PM",
          time: "3:30 PM"
        },
        {
          id: 4,
          sender: "patient",
          senderName: "Patient 48",
          message: "When would be a good time for a check-up?",
          timestamp: "Aug 26, 4:45 PM",
          time: "4:45 PM"
        }
      ]
    }
  ];

  const handleChatSelect = (chatId) => {
    setSelectedChatId(chatId);
  };

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      // In a real app, this would send the message to the server
      console.log('Sending message:', newMessage);
      setNewMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredChats = chats.filter(chat =>
    chat.patientName.toLowerCase().includes(chatSearchTerm.toLowerCase()) ||
    chat.lastMessage.toLowerCase().includes(chatSearchTerm.toLowerCase())
  );

  // Mock video data for selected patient
  // Patient-specific video data - only certain patients have videos
  const patientVideosData = {
    42: [
      {
        id: 1,
        title: "Evening Medication Schedule",
        timestamp: "Aug 28, 2025, 8:45 PM",
        duration: "2:45",
        thumbnail: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=200&h=120&fit=crop&auto=format",
        status: "completed",
        medications: ["Tylenol", "Atorvastatin", "Advil", "Levothyroxine"]
      },
      {
        id: 2,
        title: "Morning Medication Schedule",
        timestamp: "Aug 28, 2025, 8:30 AM", 
        duration: "4:12",
        thumbnail: "https://images.unsplash.com/photo-1584362917165-526a968579e8?w=200&h=120&fit=crop&auto=format",
        status: "completed",
        medications: ["Metformin", "Lisinopril"]
      },
      {
        id: 3,
        title: "Evening Medication Schedule",
        timestamp: "Aug 27, 2025, 8:45 PM",
        duration: "1:58", 
        thumbnail: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=200&h=120&fit=crop&auto=format",
        status: "completed",
        medications: ["Tylenol", "Atorvastatin", "Advil"]
      }
    ],
    44: [
      {
        id: 1,
        title: "Morning Medication Schedule", 
        timestamp: "Aug 27, 2025, 8:30 AM",
        duration: "3:22",
        thumbnail: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=200&h=120&fit=crop&auto=format",
        status: "missed",
        medications: ["Metformin", "Lisinopril"]
      },
      {
        id: 2,
        title: "Evening Medication Schedule",
        timestamp: "Aug 26, 2025, 8:45 PM",
        duration: "2:15",
        thumbnail: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=200&h=120&fit=crop&auto=format",
        status: "partial",
        medications: ["Tylenol", "Advil"]
      }
    ],
    48: [
      {
        id: 1,
        title: "Evening Medication Schedule",
        timestamp: "Aug 28, 2025, 8:45 PM",
        duration: "3:10",
        thumbnail: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=200&h=120&fit=crop&auto=format",
        status: "completed",
        medications: ["Atorvastatin", "Levothyroxine"]
      }
    ],
    63: [
      {
        id: 1,
        title: "Morning Medication Schedule",
        timestamp: "Aug 28, 2025, 9:15 AM",
        duration: "4:05",
        thumbnail: "https://images.unsplash.com/photo-1584362917165-526a968579e8?w=200&h=120&fit=crop&auto=format",
        status: "completed",
        medications: ["Metformin", "Lisinopril", "Sertraline"]
      },
      {
        id: 2,
        title: "Evening Medication Schedule",
        timestamp: "Aug 27, 2025, 8:30 PM",
        duration: "2:55",
        thumbnail: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=200&h=120&fit=crop&auto=format",
        status: "completed",
        medications: ["Atorvastatin", "Levothyroxine"]
      }
    ]
  };

  const getPatientVideos = (patientId) => {
    if (!patientId) return [];
    return patientVideosData[patientId] || [];
  };

  const handleVideoPlay = (video) => {
    setSelectedVideo(video);
    setVideoModalOpen(true);
  };

  const handleVideoModalClose = () => {
    setVideoModalOpen(false);
    setSelectedVideo(null);
  };

  // Resizer handlers
  const handleMouseDown = (e) => {
    console.log('Mouse down on resizer:', e.clientY);
    e.preventDefault();
    e.stopPropagation();
    
    const startY = e.clientY;
    const startHeight = patientListHeight;

    const handleMouseMove = (e) => {
      e.preventDefault();
      const deltaY = e.clientY - startY;
      const newHeight = Math.max(200, Math.min(600, startHeight + deltaY)); // Min 200px, max 600px
      console.log('Mouse move - deltaY:', deltaY, 'newHeight:', newHeight, 'current state:', patientListHeight, 'section height:', newHeight + 80);
      setPatientListHeight(newHeight);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = ''; // Re-enable text selection
      document.body.style.cursor = ''; // Reset cursor
    };

    // Disable text selection and set cursor during drag
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'row-resize';

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Patient-specific reconciliation data
  const patientReconciliationData = {
    42: [
      { group: '95243', id: '342137', scheduleTime: '9/22/2025 6:00 pm', away: false, adHoc: false, medicationName: 'Advil', status: 'TAKEN', time: '6:05 pm' },
      { group: '95243', id: '342136', scheduleTime: '9/22/2025 6:00 pm', away: false, adHoc: false, medicationName: 'Tylenol', status: 'TAKEN', time: '6:05 pm' },
      { group: '95214', id: '342069', scheduleTime: '9/22/2025 3:00 am', away: false, adHoc: false, medicationName: 'Advil', status: 'TAKEN', time: '3:02 am' },
      { group: '95214', id: '342068', scheduleTime: '9/22/2025 3:00 am', away: false, adHoc: false, medicationName: 'Tylenol', status: 'TAKEN', time: '3:02 am' },
      { group: '95200', id: '342036', scheduleTime: '9/21/2025 6:00 pm', away: false, adHoc: false, medicationName: 'Advil', status: 'TAKEN', time: '6:15 pm' },
      { group: '95200', id: '342035', scheduleTime: '9/21/2025 6:00 pm', away: false, adHoc: false, medicationName: 'Tylenol', status: 'TAKEN', time: '6:15 pm' }
    ],
    44: [
      { group: '95243', id: '342137', scheduleTime: '9/22/2025 6:00 pm', away: false, adHoc: false, medicationName: 'Lisinopril', status: 'MISSED', time: '' },
      { group: '95214', id: '342069', scheduleTime: '9/22/2025 3:00 am', away: false, adHoc: false, medicationName: 'Metformin', status: 'MISSED', time: '' },
      { group: '95200', id: '342036', scheduleTime: '9/21/2025 6:00 pm', away: false, adHoc: false, medicationName: 'Lisinopril', status: 'MISSED', time: '' },
      { group: '95171', id: '341967', scheduleTime: '9/21/2025 3:00 am', away: false, adHoc: false, medicationName: 'Metformin', status: 'TAKEN', time: '4:30 am' }
    ],
    63: [
      { group: '95243', id: '342137', scheduleTime: '9/22/2025 6:00 pm', away: false, adHoc: false, medicationName: 'Atorvastatin', status: 'TAKEN', time: '6:02 pm' },
      { group: '95243', id: '342136', scheduleTime: '9/22/2025 6:00 pm', away: false, adHoc: false, medicationName: 'Levothyroxine', status: 'TAKEN', time: '6:02 pm' },
      { group: '95243', id: '342135', scheduleTime: '9/22/2025 6:00 pm', away: false, adHoc: false, medicationName: 'Loratadine', status: 'TAKEN', time: '6:02 pm' },
      { group: '95214', id: '342069', scheduleTime: '9/22/2025 3:00 am', away: false, adHoc: false, medicationName: 'Sertraline', status: 'TAKEN', time: '3:05 am' },
      { group: '95214', id: '342068', scheduleTime: '9/22/2025 3:00 am', away: false, adHoc: false, medicationName: 'Metformin', status: 'TAKEN', time: '3:05 am' }
    ]
  };

  const getPatientReconciliation = (patientId) => {
    if (!patientId) return [];
    return patientReconciliationData[patientId] || [];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Online': return '#16a34a';
      case 'Offline': return '#6b7280';
      case 'Not Assigned': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getStatusTextColor = (status) => {
    return status === 'Not Assigned' ? '#92400e' : 'white';
  };

  const getAdherenceColor = (adherence) => {
    if (adherence >= 80) return '#16a34a';
    if (adherence >= 60) return '#f59e0b';
    return '#dc2626';
  };

    return (
      <div className="patient-overview">
        <div className="overview-content">
        
        {/* Header Section */}
        <div className="users-header">
          <h1>Patient Analysis</h1>
        </div>

        {/* Filters Section */}
        <div className="users-filters">
          <div className="search-filter">
            <Search className="search-icon" size={16} />
            <input
              type="text"
              placeholder="Filter by keyword"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="filter-search"
            />
          </div>
          
          <div className="filter-dropdowns">
            <select 
              value={selectedFilter} 
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="filter-dropdown"
            >
              <option>All selected (7)</option>
              <option>Online only</option>
              <option>Offline only</option>
            </select>
            
            <select 
              value={segmentFilter} 
              onChange={(e) => setSegmentFilter(e.target.value)}
              className="filter-dropdown"
            >
              <option>Any segment</option>
              <option>High adherence</option>
              <option>Low adherence</option>
            </select>
            
            <select 
              value={tableTimeFilter} 
              onChange={(e) => setTableTimeFilter(e.target.value)}
              className="filter-dropdown"
            >
              <option>Last 7 days</option>
              <option>Last 30 days</option>
              <option>Last 90 days</option>
            </select>
            
            <button className="more-filters">
              <Filter size={16} />
              More filters
              <ChevronDown size={14} />
            </button>
          </div>
        </div>

        {/* Stats Section */}
        <div className="users-stats" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Showing {patients.length} patients</span>
          <div className="view-toggle">
            <button 
              className={`view-toggle-btn ${viewType === 'detailed' ? 'active' : ''}`}
              onClick={() => setViewType('detailed')}
              title="Detailed View"
            >
              <List size={16} />
              Detailed
            </button>
            <button 
              className={`view-toggle-btn ${viewType === 'simple' ? 'active' : ''}`}
              onClick={() => setViewType('simple')}
              title="Simple View"
            >
              <Table size={16} />
              Simple
            </button>
          </div>
        </div>

        {/* Patient Table */}
        <div 
          className="patient-table-section" 
          style={{ 
            height: `${patientListHeight}px`,
            minHeight: `${patientListHeight}px`,
            maxHeight: `${patientListHeight}px`
          }}
        >
          {viewType === 'detailed' ? (
            <div 
              className="patient-table-container" 
              style={{ 
                height: `${patientListHeight}px`,
                minHeight: `${patientListHeight}px`,
                maxHeight: `${patientListHeight}px`
              }}
            >
              <table className="users-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handlePatientSort('id')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div>ID</div>
                        <div>{renderPatientSortIcon('id')}</div>
                      </div>
                    </th>
                    <th className="sortable" onClick={() => handlePatientSort('weekTrend')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div>2wk Trend</div>
                        <div>{renderPatientSortIcon('weekTrend')}</div>
                      </div>
                    </th>
                    <th className="sortable" onClick={() => handlePatientSort('scheduleAdherence')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div>Schedule Adherence</div>
                        <div>{renderPatientSortIcon('scheduleAdherence')}</div>
                      </div>
                    </th>
                    <th className="sortable" onClick={() => handlePatientSort('overallAdherence')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div>Overall Adherence</div>
                        <div>{renderPatientSortIcon('overallAdherence')}</div>
                      </div>
                    </th>
                    <th className="sortable" onClick={() => handlePatientSort('dosingOpportunities')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div>Dosing Opportunities</div>
                        <div>{renderPatientSortIcon('dosingOpportunities')}</div>
                      </div>
                    </th>
                    <th className="sortable" onClick={() => handlePatientSort('daysInTreatment')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div>Days in Treatment</div>
                        <div>{renderPatientSortIcon('daysInTreatment')}</div>
                      </div>
                    </th>
                    <th className="sortable" onClick={() => handlePatientSort('doseAdminTime')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div>Dose Admin. Time</div>
                        <div>{renderPatientSortIcon('doseAdminTime')}</div>
                      </div>
                    </th>
                    <th>Away</th>
                    <th className="sortable" onClick={() => handlePatientSort('pillboxStatus')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div>Pillbox Status</div>
                        <div>{renderPatientSortIcon('pillboxStatus')}</div>
                      </div>
                    </th>
                    <th className="sortable" onClick={() => handlePatientSort('nextFollowUp')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div>Next Follow Up</div>
                        <div>{renderPatientSortIcon('nextFollowUp')}</div>
                      </div>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPatients.map((patient) => (
                    <tr 
                      key={patient.id} 
                      className={`user-row ${selectedPatient?.id === patient.id ? 'selected' : ''}`}
                      onClick={() => handlePatientSelect(patient)}
                    >
                      <td>
                        <div className="user-id-cell">
                          {patient.avatar ? (
                            <img src={patient.avatar} alt={`User ${patient.id}`} className="user-avatar" />
                          ) : (
                            <div className="user-avatar-placeholder">
                              <div className="avatar-icon"></div>
                            </div>
                          )}
                          <span className="user-id">{patient.id}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                          {patient.weekTrend.startsWith('+') ? (
                            <ArrowUpCircle size={16} style={{ color: '#16a34a' }} />
                          ) : patient.weekTrend.startsWith('-') ? (
                            <ArrowDownCircle size={16} style={{ color: '#dc2626' }} />
                          ) : null}
                          <span>{patient.weekTrend}</span>
                        </div>
                      </td>
                      <td>{patient.scheduleAdherence}</td>
                      <td>{patient.scheduleAdherence}</td>
                      <td>
                        {(() => {
                          const parts = patient.dosingOpportunities.split(' / ');
                          if (parts.length === 2) {
                            const first = parseInt(parts[0]);
                            const second = parseInt(parts[1]);
                            const diff = first - second;
                            return `${patient.dosingOpportunities} (${diff})`;
                          }
                          return patient.dosingOpportunities;
                        })()}
                      </td>
                      <td>{patient.daysInTreatment}</td>
                      <td>{patient.doseAdminTime}</td>
                      <td>
                        <span className="away-indicator">×</span>
                      </td>
                      <td>
                        <span 
                          className="status-badge"
                          style={{ 
                            backgroundColor: getStatusColor(patient.pillboxStatus),
                            color: getStatusTextColor(patient.pillboxStatus)
                          }}
                        >
                          {patient.pillboxStatus}
                        </span>
                      </td>
                      <td>{patient.nextFollowUp}</td>
                      <td>
                        <button className="actions-button" onClick={(e) => e.stopPropagation()}>
                          <MoreHorizontal size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div 
              className="patient-cards-container" 
              style={{ 
                height: `${patientListHeight}px`,
                minHeight: `${patientListHeight}px`,
                maxHeight: `${patientListHeight}px`
              }}
            >
              {patients.map((patient) => (
                <div
                  key={patient.id}
                  className={`patient-card ${selectedPatient?.id === patient.id ? 'selected' : ''}`}
                  onClick={() => handlePatientSelect(patient)}
                >
                  <div className="patient-card-header">
                    <div className="patient-name-section">
                      {patient.avatar && (
                        <img src={patient.avatar} alt={patient.name} className="patient-avatar-large" />
                      )}
                      <div className="patient-name-info">
                        <h3>{patient.name}</h3>
                        <span className="days-in-treatment">{patient.daysInTreatment} days in treatment</span>
                      </div>
                    </div>
                    <div className="patient-indicators">
                      {patient.hasAlerts && (
                        <AlertTriangle size={16} className="alert-icon" />
                      )}
                      <div 
                        className="status-indicator-large"
                        style={{ backgroundColor: getStatusColor(patient.status) }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="patient-stats">
                    <div className="stat">
                      <TrendingUp size={14} />
                      <span 
                        className="adherence"
                        style={{ color: getAdherenceColor(patient.adherence) }}
                      >
                        {patient.adherence}% adherence
                      </span>
                    </div>
                    <div className="stat">
                      <Clock size={14} />
                      <span className="last-dose">{patient.lastDose}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Status:</span>
                      <span className={`pillbox-status ${patient.status}`}>
                        {patient.pillboxStatus}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Resizer Handle */}
        <div 
          className="patient-list-resizer"
          onMouseDown={handleMouseDown}
          title="Drag to resize patient list"
        >
          <div className="resizer-handle">
            <div className="resizer-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        </div>

        {/* Chart Panel */}
        <div className="chart-panel">
            <div className="chart-header">
              <div className="chart-title-section">
                <div className="chart-title-row">
                  <h2>
                    {chartType === 'doseTime' ? 'Dose Administration Time' : 
                     chartType === 'adherence' ? 'Adherence by Medication' : 
                     chartType === 'videos' ? 'Patient Videos' :
                     chartType === 'chats' ? 'Patient Communications' :
                     chartType === 'notes' ? 'Clinical Notes' :
                     chartType === 'reconciliation' ? 'Medication Reconciliation' :
                     'Dashboard'}
                  </h2>
                </div>
                <div className="chart-controls-row">
                  <div className="chart-type-toggle">
                    <button
                      className={`toggle-btn ${chartType === 'doseTime' ? 'active' : ''}`}
                      onClick={() => handleChartTypeChange('doseTime')}
                    >
                      <Clock size={16} />
                      Dose Time
                    </button>
                    <button
                      className={`toggle-btn ${chartType === 'adherence' ? 'active' : ''}`}
                      onClick={() => handleChartTypeChange('adherence')}
                    >
                      <TrendingUp size={16} />
                      Adherence
                    </button>
                    <button
                      className={`toggle-btn ${chartType === 'videos' ? 'active' : ''}`}
                      onClick={() => handleChartTypeChange('videos')}
                    >
                      <Play size={16} />
                      Videos
                    </button>
                    <button
                      className={`toggle-btn ${chartType === 'chats' ? 'active' : ''}`}
                      onClick={() => handleChartTypeChange('chats')}
                    >
                      <MessageCircle size={16} />
                      Chats
                    </button>
                    <button
                      className={`toggle-btn ${chartType === 'notes' ? 'active' : ''}`}
                      onClick={() => handleChartTypeChange('notes')}
                    >
                      <FileText size={16} />
                      Notes
                    </button>
                    <button
                      className={`toggle-btn ${chartType === 'reconciliation' ? 'active' : ''}`}
                      onClick={() => handleChartTypeChange('reconciliation')}
                    >
                      <ClipboardCheck size={16} />
                      Reconciliation
                    </button>
                  </div>
                  
                  {chartType === 'adherence' && (
                    <div className="time-filter-dropdown-inline">
                      <select 
                        value={timeFilter} 
                        onChange={(e) => setTimeFilter(e.target.value)}
                        className="time-filter-select-inline"
                      >
                        <option value="3months">Trailing 3 months</option>
                        <option value="6months">Trailing 6 months</option>
                        <option value="1year">Trailing 1 year</option>
                      </select>
                    </div>
                  )}
                  
                  {/* Selected patient indicator hidden - now shown in left panel */}
                </div>
              </div>
              {chartType === 'doseTime' && (
                <div className="chart-legend">
                  <div className="legend-item">
                    <div className="legend-color all-patients"></div>
                    <span>All Patients</span>
                  </div>
                  {selectedPatient && (
                    <div className="legend-item">
                      <div className="legend-color selected-patient"></div>
                      <span>{selectedPatient.name}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          
          {chartType === 'doseTime' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info - Only show when patient is selected */}
              {selectedPatient && (
                <div className="patient-info-panel">
                  <div className="patient-info-header">
                    <div className="patient-avatar-section">
                      {selectedPatient.avatar && (
                        <img src={selectedPatient.avatar} alt={selectedPatient.name} className="patient-avatar-large" />
                      )}
                      <div className="patient-details">
                        <h3>{selectedPatient.name}</h3>
                        <span className="patient-id">ID: {selectedPatient.id}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="patient-stats-list">
                    <div className="stat-row">
                      <span className="stat-label">STATUS:</span>
                      <span className={`status-badge ${selectedPatient.status}`}>
                        {selectedPatient.status === 'online' ? 'ONLINE' : 
                         selectedPatient.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">ADHERENCE:</span>
                      <span className="adherence-value" style={{ color: getAdherenceColor(selectedPatient.adherence) }}>
                        {selectedPatient.adherence}%
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">LAST DOSE:</span>
                      <span className="stat-value">{selectedPatient.lastDose}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Right Panel - Chart */}
              <div className="main-content-panel">
                {selectedPatient ? (
                  <div className="chart-container-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                          dataKey="time"
                          stroke="#6b7280"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="#6b7280"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Area
                          type="monotone"
                          dataKey="allPatients"
                          stackId="1"
                          stroke="#64748b"
                          fill="#64748b"
                          fillOpacity={0.6}
                        />
                        {selectedPatient && (
                          <Area
                            type="monotone"
                            dataKey="selectedPatient"
                            stackId="2"
                            stroke="#f97316"
                            fill="#f97316"
                            fillOpacity={0.8}
                          />
                        )}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="no-patient-selected">
                    <Clock size={48} className="placeholder-icon" />
                    <h3>Select a patient to view dose times</h3>
                    <p>Choose a patient from the table above to see their medication administration schedule</p>
                  </div>
                )}
              </div>
            </div>
          ) : chartType === 'adherence' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info - Only show when patient is selected */}
              {selectedPatient && (
                <div className="patient-info-panel">
                  <div className="patient-info-header">
                    <div className="patient-avatar-section">
                      {selectedPatient.avatar && (
                        <img src={selectedPatient.avatar} alt={selectedPatient.name} className="patient-avatar-large" />
                      )}
                      <div className="patient-details">
                        <h3>{selectedPatient.name}</h3>
                        <span className="patient-id">ID: {selectedPatient.id}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="patient-stats-list">
                    <div className="stat-row">
                      <span className="stat-label">STATUS:</span>
                      <span className={`status-badge ${selectedPatient.status}`}>
                        {selectedPatient.status === 'online' ? 'ONLINE' : 
                         selectedPatient.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">ADHERENCE:</span>
                      <span className="adherence-value" style={{ color: getAdherenceColor(selectedPatient.adherence) }}>
                        {selectedPatient.adherence}%
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">LAST DOSE:</span>
                      <span className="stat-value">{selectedPatient.lastDose}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Middle Panel - Medication Controls - Only show when patient is selected */}
              {selectedPatient && (
                <div className="controls-panel-tabs">
                  <div className="medication-controls-sidebar">
                  <div className="medication-control-header">
                    <div className="control-buttons">
                      <button className="control-btn" onClick={handleSelectAll}>Select All</button>
                      <button className="control-btn" onClick={handleSelectNone}>Select None</button>
                    </div>
                  </div>
                  <div className="medication-list">
                    {medications.map((med) => (
                      <div 
                        key={med.name} 
                        className={`medication-item ${selectedMedications[med.name] ? 'selected' : ''}`}
                        onClick={() => handleMedicationToggle(med.name)}
                      >
                        <div className="medication-checkbox">
                          <div className={`checkbox ${selectedMedications[med.name] ? 'checked' : ''}`}>
                            {selectedMedications[med.name] && '✓'}
                          </div>
                        </div>
                        <div 
                          className="medication-color" 
                          style={{ backgroundColor: med.color }}
                        ></div>
                        <span className="medication-name">{med.name}</span>
                        <span className="medication-percentage">{med.baseAdherence}%</span>
                      </div>
                    ))}
                  </div>
                  </div>
                </div>
              )}

              {/* Right Panel - Chart */}
              <div className="main-content-panel">
                {selectedPatient ? (
                  <div className="chart-container-with-sidebar">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                          dataKey="month"
                          stroke="#6b7280"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke="#6b7280"
                          fontSize={12}
                          tickLine={false}
                          axisLine={false}
                          domain={[50, 100]}
                          tickFormatter={(value) => `${value}%`}
                        />
                        {medications.map((med) => (
                          selectedMedications[med.name] && (
                            <Line
                              key={med.name}
                              type="monotone"
                              dataKey={med.name}
                              stroke={med.color}
                              strokeWidth={2}
                              dot={{ r: 2, fill: med.color }}
                              activeDot={{ r: 3, fill: med.color }}
                            />
                          )
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="no-patient-selected">
                    <TrendingUp size={48} className="placeholder-icon" />
                    <h3>Select a patient to view adherence data</h3>
                    <p>Choose a patient from the table above to see their medication adherence trends</p>
                  </div>
                )}
              </div>
            </div>
          ) : chartType === 'videos' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info - Only show when patient is selected */}
              {selectedPatient && (
                <div className="patient-info-panel">
                  <div className="patient-info-header">
                    <div className="patient-avatar-section">
                      {selectedPatient.avatar && (
                        <img src={selectedPatient.avatar} alt={selectedPatient.name} className="patient-avatar-large" />
                      )}
                      <div className="patient-details">
                        <h3>{selectedPatient.name}</h3>
                        <span className="patient-id">ID: {selectedPatient.id}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="patient-stats-list">
                    <div className="stat-row">
                      <span className="stat-label">STATUS:</span>
                      <span className={`status-badge ${selectedPatient.status}`}>
                        {selectedPatient.status === 'online' ? 'ONLINE' : 
                         selectedPatient.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">ADHERENCE:</span>
                      <span className="adherence-value" style={{ color: getAdherenceColor(selectedPatient.adherence) }}>
                        {selectedPatient.adherence}%
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">LAST DOSE:</span>
                      <span className="stat-value">{selectedPatient.lastDose}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Right Panel - Videos */}
              <div className="main-content-panel">
                <div className="videos-container">
                  {selectedPatient ? (
                    (() => {
                      const patientVideos = getPatientVideos(selectedPatient.id);
                      return patientVideos.length > 0 ? (
                        <div className="videos-list">
                          <div className="videos-header">
                            <h3>Videos for {selectedPatient.name}</h3>
                          </div>
                          <div className="videos-grid">
                        {patientVideos.map((video) => (
                          <div key={video.id} className="video-card" onClick={() => handleVideoPlay(video)}>
                            <div className="video-thumbnail">
                              <img src={video.thumbnail} alt={video.title} />
                              <div className="video-duration">{video.duration}</div>
                              <div className={`video-status-indicator ${video.status}`}></div>
                              <div className="video-play-overlay">
                                <Play size={32} fill="white" />
                              </div>
                            </div>
                            <div className="video-card-info">
                              <h4>{video.title}</h4>
                              <p className="video-timestamp">{video.timestamp}</p>
                              <div className="video-medications">
                                {video.medications.map((medication, index) => (
                                  <span key={index} className="medication-tag-small">
                                    {medication}
                                  </span>
                                ))}
                              </div>
                              <span className={`video-status-text ${video.status}`}>
                                {video.status === 'completed' && '✓ Completed'}
                                {video.status === 'missed' && '✗ Missed'}
                                {video.status === 'partial' && '~ Partial'}
                              </span>
                            </div>
                          </div>
                        ))}
                          </div>
                        </div>
                      ) : (
                        <div className="no-patient-selected">
                          <Play size={48} className="placeholder-icon" />
                          <h3>No videos available</h3>
                          <p>{selectedPatient.name} does not have any medication videos recorded yet</p>
                        </div>
                      );
                    })()
                  ) : (
                  <div className="no-patient-selected">
                    <Play size={48} className="placeholder-icon" />
                    <h3>Select a patient to view videos</h3>
                    <p>Choose a patient from the table above to see their medication videos</p>
                  </div>
                )}
                </div>
              </div>
            </div>
          ) : chartType === 'chats' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info - Only show when patient is selected */}
              {selectedPatient && (
                <div className="patient-info-panel">
                  <div className="patient-info-header">
                    <div className="patient-avatar-section">
                      {selectedPatient.avatar && (
                        <img src={selectedPatient.avatar} alt={selectedPatient.name} className="patient-avatar-large" />
                      )}
                      <div className="patient-details">
                        <h3>{selectedPatient.name}</h3>
                        <span className="patient-id">ID: {selectedPatient.id}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="patient-stats-list">
                    <div className="stat-row">
                      <span className="stat-label">STATUS:</span>
                      <span className={`status-badge ${selectedPatient.status}`}>
                        {selectedPatient.status === 'online' ? 'ONLINE' : 
                         selectedPatient.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">ADHERENCE:</span>
                      <span className="adherence-value" style={{ color: getAdherenceColor(selectedPatient.adherence) }}>
                        {selectedPatient.adherence}%
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">LAST DOSE:</span>
                      <span className="stat-value">{selectedPatient.lastDose}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Right Panel - Chat Messages */}
              <div className="main-content-panel">
                {selectedPatient ? (
                  (() => {
                    const patientChat = chats.find(c => c.patientId === selectedPatient.id);
                    return patientChat ? (
                      <div className="chat-right-panel">
                        <div className="chat-messages">
                          {patientChat.messages.map((message) => (
                            <div
                              key={message.id}
                              className={`message ${message.sender === 'doctor' ? 'sent' : 'received'}`}
                            >
                              <div className="message-content">
                                <p>{message.message}</p>
                                <span className="message-time">{message.time}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="chat-input-container">
                          <div className="chat-input-wrapper">
                            <input
                              type="text"
                              placeholder="Type a message..."
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              onKeyPress={handleKeyPress}
                              className="chat-input"
                            />
                            <button
                              className="send-button"
                              onClick={handleSendMessage}
                              disabled={!newMessage.trim()}
                            >
                              <Send size={20} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="no-chat-available">
                        <MessageCircle size={48} className="placeholder-icon" />
                        <h3>No conversations available</h3>
                        <p>No chat history found for {selectedPatient.name}</p>
                      </div>
                    );
                  })()
                ) : (
                  <div className="no-patient-selected">
                    <MessageCircle size={48} className="placeholder-icon" />
                    <h3>Select a patient to view chats</h3>
                    <p>Choose a patient from the table above to see their conversation history</p>
                  </div>
                )}
              </div>
            </div>
          ) : chartType === 'notes' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info - Only show when patient is selected */}
              {selectedPatient && (
                <div className="patient-info-panel">
                  <div className="patient-info-header">
                    <div className="patient-avatar-section">
                      {selectedPatient.avatar && (
                        <img src={selectedPatient.avatar} alt={selectedPatient.name} className="patient-avatar-large" />
                      )}
                      <div className="patient-details">
                        <h3>{selectedPatient.name}</h3>
                        <span className="patient-id">ID: {selectedPatient.id}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="patient-stats-list">
                    <div className="stat-row">
                      <span className="stat-label">STATUS:</span>
                      <span className={`status-badge ${selectedPatient.status}`}>
                        {selectedPatient.status === 'online' ? 'ONLINE' : 
                         selectedPatient.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">ADHERENCE:</span>
                      <span className="adherence-value" style={{ color: getAdherenceColor(selectedPatient.adherence) }}>
                        {selectedPatient.adherence}%
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">LAST DOSE:</span>
                      <span className="stat-value">{selectedPatient.lastDose}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Middle Panel - Notes List - Only show when patient is selected and has notes */}
              {selectedPatient && filteredNotes.length > 0 && (
                <>
                  <div className="controls-panel">
                    <div className="notes-left-panel">
                      <div className="notes-header">
                        <h3>Notes</h3>
                        <button className="new-note-btn">
                          <Plus size={16} />
                          New Note
                        </button>
                      </div>
                      
                      <div className="notes-search">
                        <div className="search-input-container">
                          <Search size={16} className="search-icon" />
                          <input
                            type="text"
                            placeholder="Search notes"
                            value={notesSearchTerm}
                            onChange={(e) => setNotesSearchTerm(e.target.value)}
                            className="notes-search-input"
                          />
                        </div>
                      </div>
                      
                      <div className="notes-list">
                        {filteredNotes.map((note) => (
                          <div
                            key={note.id}
                            className={`note-list-item ${note.id === selectedNoteId ? 'active' : ''}`}
                            onClick={() => handleNoteSelect(note.id)}
                          >
                            <div className="note-item-header">
                              <h4 className="note-item-title">{note.title}</h4>
                              <span className="note-item-date">{note.date}</span>
                            </div>
                            <div className="note-item-meta">
                              <span className="note-author">By: {note.author}</span>
                              {note.patientId && (
                                <span className="note-patient">Patient: {note.patientId}</span>
                              )}
                            </div>
                            <p className="note-item-summary">{note.summary}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Panel - Note Details */}
                  <div className="main-content-panel">
                    <div className="notes-right-panel">
                      {(() => {
                        const selectedNote = notes.find(n => n.id === selectedNoteId);
                        return selectedNote ? (
                          <div className="note-details">
                            <div className="note-details-header">
                              <div className="note-details-title">
                                <h3>{selectedNote.title} Patient:{selectedNote.patientId}</h3>
                                <div className="note-details-meta">
                                  <span>Reviewed by: {selectedNote.author}</span>
                                  <span>Date: {selectedNote.date}</span>
                                </div>
                              </div>
                              <div className="note-actions">
                                <button className="note-action-btn">
                                  <Edit size={16} />
                                </button>
                                <button className="note-action-btn">
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                            
                            <div className="note-content">
                              <h4>{selectedNote.summary}</h4>
                              <div className="note-text">
                                {selectedNote.content.split('\n\n').map((paragraph, index) => (
                                  <p key={index}>{paragraph}</p>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="no-note-selected">
                            <FileText size={48} className="placeholder-icon" />
                            <h3>Select a note to view details</h3>
                            <p>Choose a note from the list to view its contents</p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </>
              )}

              {/* Show "no notes" message when patient has no notes */}
              {selectedPatient && filteredNotes.length === 0 && (
                <div className="main-content-panel">
                  <div className="no-patient-selected">
                    <FileText size={48} className="placeholder-icon" />
                    <h3>No notes available</h3>
                    <p>{selectedPatient.name} does not have any clinical notes on file yet</p>
                  </div>
                </div>
              )}

              {/* Main Content Panel - Show centered icon when no patient selected */}
              {!selectedPatient && (
                <div className="main-content-panel">
                  <div className="no-patient-selected">
                    <FileText size={48} className="placeholder-icon" />
                    <h3>Select a patient to view notes</h3>
                    <p>Choose a patient from the table above to see their clinical notes</p>
                  </div>
                </div>
              )}
            </div>
          ) : chartType === 'reconciliation' ? (
            <div className="three-column-layout">
              {/* Left Panel - Patient Info - Only show when patient is selected */}
              {selectedPatient && (
                <div className="patient-info-panel">
                  <div className="patient-info-header">
                    <div className="patient-avatar-section">
                      {selectedPatient.avatar && (
                        <img src={selectedPatient.avatar} alt={selectedPatient.name} className="patient-avatar-large" />
                      )}
                      <div className="patient-details">
                        <h3>{selectedPatient.name}</h3>
                        <span className="patient-id">ID: {selectedPatient.id}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="patient-stats-list">
                    <div className="stat-row">
                      <span className="stat-label">STATUS:</span>
                      <span className={`status-badge ${selectedPatient.status}`}>
                        {selectedPatient.status === 'online' ? 'ONLINE' : 
                         selectedPatient.status === 'offline' ? 'OFFLINE' : 'NOT ASSIGNED'}
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">ADHERENCE:</span>
                      <span className="adherence-value" style={{ color: getAdherenceColor(selectedPatient.adherence) }}>
                        {selectedPatient.adherence}%
                      </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">LAST DOSE:</span>
                      <span className="stat-value">{selectedPatient.lastDose}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Right Panel - Reconciliation Table */}
              {selectedPatient ? (
                (() => {
                  const reconciliationData = getPatientReconciliation(selectedPatient.id);
                  return reconciliationData.length > 0 ? (
                    <div className="main-content-panel">
                      <div className="reconciliation-container">
                    <div className="reconciliation-table-wrapper">
                      <div className="table-controls">
                        <div className="table-controls-left">
                          <button className="control-btn">
                            <Columns size={16} />
                            Columns
                          </button>
                          <button className="control-btn">
                            <Filter size={16} />
                            Filters
                            <span className="filter-badge">1</span>
                          </button>
                          <button className="control-btn">
                            <MoreHorizontal size={16} />
                            Density
                          </button>
                          <button className="control-btn">
                            <Download size={16} />
                            Export
                          </button>
                        </div>
                      </div>
                      
                      <div className="medication-table-container">
                        <table className="medication-table">
                          <thead>
                            <tr>
                              <th>Group</th>
                              <th>ID <TrendingUp size={12} className="sort-icon" /></th>
                              <th>Schedule Time</th>
                              <th>Away</th>
                              <th>Ad Hoc</th>
                              <th>Medication Name</th>
                              <th>Status <TrendingUp size={12} className="sort-icon" /></th>
                              <th>Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reconciliationData.map((row, index) => (
                              <tr key={index} className="medication-row">
                                <td>{row.group}</td>
                                <td>{row.id}</td>
                                <td>{row.scheduleTime}</td>
                                <td>
                                  <span className="status-x">×</span>
                                </td>
                                <td>
                                  <span className="status-x">×</span>
                                </td>
                                <td className="medication-name">{row.medicationName}</td>
                                <td>
                                  <span className={`status-badge ${row.status.toLowerCase()}`}>
                                    {row.status}
                                  </span>
                                </td>
                                <td className="time-cell">{row.time}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                      </div>
                    </div>
                  ) : (
                    <div className="main-content-panel">
                      <div className="no-patient-selected">
                        <ClipboardCheck size={48} className="placeholder-icon" />
                        <h3>No reconciliation data available</h3>
                        <p>{selectedPatient.name} does not have any medication reconciliation records yet</p>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="main-content-panel">
                  <div className="no-patient-selected">
                    <ClipboardCheck size={48} className="placeholder-icon" />
                    <h3>Select a patient to view reconciliation</h3>
                    <p>Choose a patient from the table above to see their medication reconciliation data</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="default-container">
              <div className="content-placeholder">
                <TrendingUp size={48} className="placeholder-icon" />
                <h3>Patient Analysis</h3>
                <p>Select a tab above to view patient data and analysis.</p>
              </div>
            </div>
          )}
          
          {!selectedPatient && chartType === 'doseTime' && (
            <div className="no-selection">
              <p>Select a patient from the table to see their dose timing pattern</p>
            </div>
          )}
        </div>

        {/* Video Modal */}
        {videoModalOpen && selectedVideo && (
          <div className="video-modal-overlay" onClick={handleVideoModalClose}>
            <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="video-modal-header">
                <h3>{selectedVideo.title}</h3>
                <button className="video-modal-close" onClick={handleVideoModalClose}>
                  ×
                </button>
              </div>
              <div className="video-modal-player">
                <div className="video-placeholder-modal">
                  <div className="video-overlay">
                    <button className="play-button-modal">
                      <Play size={64} fill="white" />
                    </button>
                  </div>
                </div>
                <div className="video-controls-modal">
                  <div className="control-group">
                    <button className="control-button">
                      <Play size={20} />
                    </button>
                    <button className="control-button">
                      <Volume2 size={20} />
                    </button>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-track">
                      <div className="progress-fill" style={{ width: '30%' }}></div>
                    </div>
                  </div>
                  <div className="control-group">
                    <span className="time-display">{selectedVideo.duration}</span>
                  </div>
                </div>
              </div>
              <div className="video-modal-info">
                <p><strong>Patient:</strong> Patient {selectedVideo.patientId}</p>
                <p><strong>Recorded:</strong> {selectedVideo.timestamp}</p>
                <p><strong>Medications:</strong> {selectedVideo.medications.join(', ')}</p>
                <p><strong>Status:</strong> <span className={`status-${selectedVideo.status}`}>{selectedVideo.status}</span></p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientOverview;
